import { useCallback, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { RollBlank } from "../models/RollBlank";
import { useTableData } from "../hooks/useTableData";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	LIST_HEADER_HEIGHT,
	LIST_ROW_HEIGHT,
	listColDef,
} from "@/controls/ag-grid/listGrid";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";

interface RollsBlankTableProps {
	rollBlanks: RollBlank[];
	selectedBlank: RollBlank | null;
	onRowSelected: (blank: RollBlank | null) => void;
	onDelete: (blank: RollBlank) => void;
}

export const RollsBlankTable = ({
	rollBlanks,
	selectedBlank,
	onRowSelected,
	onDelete,
}: RollsBlankTableProps) => {
	const gridRef = useRef<AgGridReact<RollBlank>>(null);
	const { columnDefs } = useTableData({ onDelete });

	const onGridReady = useCallback(
		(_params: GridReadyEvent<RollBlank>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<RollBlank>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<RollBlank>(onRowSelected);

	const getRowId = useCallback((params: { data: RollBlank }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<RollBlank>
				ref={gridRef}
				rowData={rollBlanks}
				columnDefs={columnDefs}
				defaultColDef={listColDef(globalDefaultColDef)}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				enableBrowserTooltips
				rowClass={LIST_ROW_CLASS}
				rowHeight={LIST_ROW_HEIGHT}
				headerHeight={LIST_HEADER_HEIGHT}
				getRowStyle={(params) => {
					if (selectedBlank?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
